import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Form, Button, Badge, ListGroup, Table, Spinner, Modal } from 'react-bootstrap';
import { Send, RefreshCw, Trash2 } from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";
import {
    getTestFlows,
    searchTestCustomers,
    triggerTestFlow,
    simulateTestTap,
    nukeTestCustomer,
    getTestFlowState
} from '../../actions/whatsappTestFlows';
import { allowWhatsappCustomer, removeWhatsappCustomer } from '../../actions/whatsappAccess';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';
import AllowlistPanel from './AllowlistPanel';
import { JOURNEY_TABS, humanize, stageLabel, journeyLabel, formatIstDateTime } from './labels';

const NO_VALUE = '-';
const NUMBER_KEYS = Array.from({ length: 9 }, (_, index) => `num_${index + 1}`);
const TIMELINE_PATH = '/inventory/v1.2/whatsapp/timeline';
const SEARCH_DEBOUNCE_MS = 300;
const NUMBER_ECHO_HINT = 'The server drops a number sent within 3 seconds of a button tap, because Wacloud echoes button titles that contain a digit, or the number had no open conversation to answer.';

const REASON_HINTS = {
    not_allowlisted: ' This customer is not on the allowlist: switch on Allow WhatsApp flows for them first.',
    opted_out: ' The customer opted out of WhatsApp.',
    suspended: ' The customer account is suspended.'
};

const outcomeText = (stage, result) => {
    const name = stageLabel(stage);
    if (result.dryRun) return { tone: 'secondary', text: `${name} recorded as a dry run. Nothing was sent.` };
    if (result.sent) return { tone: 'success', text: `${name} sent to WhatsApp (${humanize(result.status || 'accepted')}, attempt ${result.attempt}).` };
    const code = result.failureCode ? `, code ${result.failureCode}` : '';
    return { tone: 'danger', text: `${name} was not sent: ${humanize(result.reason || 'unknown')}${code}.${REASON_HINTS[result.reason] || ''}` };
};

const TONE_CLASSES = {
    success: 'bg-success-subtle text-success-emphasis',
    secondary: 'bg-secondary-subtle text-secondary-emphasis',
    danger: 'bg-danger-subtle text-danger-emphasis',
    customer: 'bg-primary text-white ms-auto',
    bot: 'bg-light border'
};

const TranscriptEntry = ({ entry }) => (
    <div className={`rounded px-3 py-2 mb-2 small ${TONE_CLASSES[entry.tone]}`} style={{ maxWidth: '85%', whiteSpace: 'pre-wrap' }}>
        {entry.text}
    </div>
);

TranscriptEntry.propTypes = {
    entry: PropTypes.shape({ tone: PropTypes.string.isRequired, text: PropTypes.string.isRequired }).isRequired
};

const customerShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    phone: PropTypes.string,
    whatsappOptOut: PropTypes.bool,
    suspend: PropTypes.bool,
    allowlisted: PropTypes.bool
});

const CustomerBadges = ({ customer }) => (
    <>
        {customer.allowlisted && <Badge bg="success" className="ms-2">Allowlisted</Badge>}
        {customer.whatsappOptOut && <Badge bg="danger" className="ms-2">Opted out</Badge>}
        {customer.suspend && <Badge bg="danger" className="ms-2">Suspended</Badge>}
    </>
);

CustomerBadges.propTypes = { customer: customerShape.isRequired };

const CustomerPicker = ({ selected, onSelect, onClear, onNuke, onToggleAllow, allowBusy, search }) => {
    const [text, setText] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const closeOnOutsideClick = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            setSearching(true);
            const rows = await search(text.trim());
            if (cancelled) return;
            setSearching(false);
            setResults(rows || []);
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [text, open, search]);

    if (selected) {
        return (
            <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="fw-semibold">{selected.name || 'Unnamed'}</span>
                <span className="text-muted">{selected.phone}</span>
                <CustomerBadges customer={selected} />
                <Form.Check
                    type="switch"
                    id="allow-whatsapp-flows"
                    label="Allow WhatsApp flows"
                    checked={Boolean(selected.allowlisted)}
                    disabled={allowBusy}
                    onChange={(event) => onToggleAllow(event.target.checked)}
                />
                <Button size="sm" variant="outline-secondary" onClick={onClear}>Change customer</Button>
                <Button size="sm" variant="outline-danger" onClick={onNuke}>Reset to new customer</Button>
            </div>
        );
    }

    const choose = (customer) => {
        setOpen(false);
        setText('');
        onSelect(customer);
    };

    return (
        <div className="position-relative" ref={wrapperRef}>
            <Form.Control
                id="test-customer"
                autoComplete="off"
                placeholder="Search a customer by name or phone"
                value={text}
                onFocus={() => setOpen(true)}
                onChange={(event) => setText(event.target.value)}
            />
            {open && (
                <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 20, maxHeight: 320, overflowY: 'auto' }}>
                    {searching && <ListGroup.Item className="text-muted small">Searching...</ListGroup.Item>}
                    {!searching && results.length === 0 && <ListGroup.Item className="text-muted small">No customers found</ListGroup.Item>}
                    {results.map((customer) => (
                        <ListGroup.Item key={customer.id} action onClick={() => choose(customer)}>
                            <span className="fw-semibold">{customer.name || 'Unnamed'}</span>
                            <span className="text-muted ms-2">{customer.phone}</span>
                            <CustomerBadges customer={customer} />
                        </ListGroup.Item>
                    ))}
                    <ListGroup.Item action className="text-center text-muted small" onClick={() => setOpen(false)}>Close</ListGroup.Item>
                </ListGroup>
            )}
        </div>
    );
};

CustomerPicker.propTypes = {
    selected: customerShape,
    onSelect: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onNuke: PropTypes.func.isRequired,
    onToggleAllow: PropTypes.func.isRequired,
    allowBusy: PropTypes.bool.isRequired,
    search: PropTypes.func.isRequired
};

const NukeModal = ({ customer, busy, onCancel, onConfirm }) => {
    const [typed, setTyped] = useState('');
    const [removeAddresses, setRemoveAddresses] = useState(false);
    const confirmed = typed === customer.phone;

    return (
        <Modal show onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title>Reset {customer.name || 'customer'} to a new customer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2">This permanently removes everything the customer did, so they behave like a newly registered user:</p>
                <ul className="small">
                    <li>All orders, payments, refunds, invoices, carts, coupons and notifications</li>
                    <li>All deliveries, can transactions, can wallet, deposits, ledgers and memberships</li>
                    <li>All WhatsApp messages, taps, offers, journeys and support requests</li>
                    <li>Referral earnings (the referral code stays)</li>
                </ul>
                <p className="small text-muted">
                    Kept: the account, name and phone (no registration again), the referral code and the node and agent stock records.
                    Stage only; there is no undo.
                </p>
                <Form.Check
                    type="checkbox"
                    id="nuke-addresses"
                    className="mb-3"
                    label="Also remove saved delivery addresses"
                    checked={removeAddresses}
                    onChange={(event) => setRemoveAddresses(event.target.checked)}
                />
                <Form.Label htmlFor="nuke-confirm" className="small">Type {customer.phone} to confirm</Form.Label>
                <Form.Control id="nuke-confirm" autoComplete="off" value={typed} onChange={(event) => setTyped(event.target.value.trim())} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button variant="danger" disabled={!confirmed || busy} onClick={() => onConfirm(removeAddresses)}>
                    {busy ? <Spinner size="sm" animation="border" /> : 'Reset customer'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

NukeModal.propTypes = {
    customer: customerShape.isRequired,
    busy: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
};

const FlowRow = ({ flow, busy, disabled, onSend, onTap }) => (
    <ListGroup.Item>
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
            <div>
                <span className="fw-semibold">{stageLabel(flow.stage)}</span>
                {flow.template && <Badge bg="light" text="dark" className="ms-2 border">{flow.template}</Badge>}
                {!flow.enabled && <Badge bg="warning" text="dark" className="ms-2">Switched off</Badge>}
            </div>
            {flow.triggerable && (
                <Button size="sm" variant="primary" disabled={disabled || Boolean(busy) || !flow.enabled} onClick={() => onSend(flow.stage)}>
                    {busy === `send:${flow.stage}` ? <Spinner size="sm" animation="border" /> : <><Send size={14} className="me-1" />Send</>}
                </Button>
            )}
        </div>
        {flow.note && <div className="text-muted small mt-1">{flow.note}</div>}
        {flow.taps.length > 0 && (
            <div className="d-flex flex-wrap gap-1 mt-2">
                {flow.taps.map((tap) => (
                    <Button
                        key={tap.key}
                        size="sm"
                        variant="outline-secondary"
                        disabled={disabled || Boolean(busy)}
                        onClick={() => onTap(tap.key, tap.label)}
                    >
                        {tap.label}
                    </Button>
                ))}
            </div>
        )}
    </ListGroup.Item>
);

FlowRow.propTypes = {
    flow: PropTypes.shape({
        stage: PropTypes.string.isRequired,
        template: PropTypes.string,
        enabled: PropTypes.bool.isRequired,
        triggerable: PropTypes.bool.isRequired,
        note: PropTypes.string,
        taps: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired
    }).isRequired,
    busy: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    disabled: PropTypes.bool.isRequired,
    onSend: PropTypes.func.isRequired,
    onTap: PropTypes.func.isRequired
};

const CustomerPanel = ({ state }) => {
    if (!state) return <EmptyState className="mb-0">Pick a customer to see their journey state.</EmptyState>;
    const { user, enrollments, conversation, messages, events } = state;
    return (
        <>
            <div className="mb-3">
                <Link to={`${TIMELINE_PATH}/${user.id}`} className="small">Full timeline</Link>
            </div>
            <div className="small text-muted mb-1">Open conversation</div>
            <div className="mb-3">
                {conversation ? <Badge bg="info">{humanize(conversation.state)}</Badge> : <span className="text-muted small">None</span>}
            </div>
            <div className="small text-muted mb-1">Journeys</div>
            <Table size="sm" className="mb-3">
                <tbody>
                    {enrollments.length === 0 && <tr><td className="text-muted">None</td></tr>}
                    {enrollments.slice(0, 6).map((row) => (
                        <tr key={row._id}>
                            <td>{journeyLabel(row.journey)}</td>
                            <td>{row.currentStage ? stageLabel(row.currentStage) : NO_VALUE}</td>
                            <td><Badge bg={row.status === 'active' ? 'success' : 'secondary'}>{humanize(row.status)}</Badge></td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <div className="small text-muted mb-1">Recent messages</div>
            <Table size="sm" className="mb-3">
                <tbody>
                    {messages.length === 0 && <tr><td className="text-muted">None</td></tr>}
                    {messages.map((row) => (
                        <tr key={row._id}>
                            <td className="text-nowrap">{formatIstDateTime(row.createdAt)}</td>
                            <td>{row.direction === 'in' ? 'Customer' : (row.stage ? stageLabel(row.stage) : humanize(row.kind))}</td>
                            <td><Badge bg={row.status === 'failed' ? 'danger' : 'secondary'}>{row.status ? humanize(row.status) : NO_VALUE}</Badge></td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <div className="small text-muted mb-1">Recent taps</div>
            <Table size="sm" className="mb-0">
                <tbody>
                    {events.length === 0 && <tr><td className="text-muted">None</td></tr>}
                    {events.map((row) => (
                        <tr key={row._id}>
                            <td className="text-nowrap">{formatIstDateTime(row.at)}</td>
                            <td>{humanize(row.choice || row.eventType)}</td>
                            <td>{row.stage ? stageLabel(row.stage) : NO_VALUE}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    );
};

CustomerPanel.propTypes = {
    state: PropTypes.shape({
        user: PropTypes.object.isRequired,
        enrollments: PropTypes.array.isRequired,
        conversation: PropTypes.object,
        messages: PropTypes.array.isRequired,
        events: PropTypes.array.isRequired
    })
};

const TestFlows = ({ getTestFlows, searchTestCustomers, triggerTestFlow, simulateTestTap, nukeTestCustomer, getTestFlowState, allowWhatsappCustomer, removeWhatsappCustomer }) => {
    const [flows, setFlows] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [customer, setCustomer] = useState(null);
    const [sendForReal, setSendForReal] = useState(true);
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [transcript, setTranscript] = useState([]);
    const [showNuke, setShowNuke] = useState(false);
    const [allowBusy, setAllowBusy] = useState(false);
    const [allowlistVersion, setAllowlistVersion] = useState(0);

    const customerId = customer ? customer.id : null;

    const loadFlows = useCallback(async () => {
        setLoadFailed(false);
        const data = await getTestFlows();
        if (!data) return setLoadFailed(true);
        setFlows(data);
    }, [getTestFlows]);

    useEffect(() => {
        loadFlows();
    }, [loadFlows]);

    const loadState = useCallback(async (userId) => {
        const data = await getTestFlowState(userId);
        setState(data);
    }, [getTestFlowState]);

    useEffect(() => {
        setState(null);
        if (customerId) loadState(customerId);
    }, [customerId, loadState]);

    const append = (...entries) => setTranscript((current) => [...current, ...entries]);

    const send = async (stage) => {
        setBusy(`send:${stage}`);
        const result = await triggerTestFlow({ userId: customerId, stage, dryRun: !sendForReal });
        setBusy(false);
        if (!result) return;
        append(outcomeText(stage, result));
        loadState(customerId);
    };

    const tap = async (key, label) => {
        setBusy(`tap:${key}`);
        const result = await simulateTestTap({ userId: customerId, key });
        setBusy(false);
        if (!result) return;
        const droppedNumber = key.startsWith('num_') && result.events.length === 0;
        const recorded = result.events.length ? `Recorded: ${result.events.map((event) => humanize(event.choice || event.eventType)).join(', ')}.` : 'Nothing was recorded.';
        const owners = flows.filter((flow) => flow.taps.some((option) => option.key === key)).map((flow) => stageLabel(flow.stage));
        const staleTap = result.events.some((event) => event.eventType === 'stale_tap');
        const silentText = staleTap
            ? `Stale tap: this button does not belong to the flow sent most recently, so the server ignored it. Send ${owners.join(' or ')} first, then tap it.`
            : droppedNumber
            ? `${NUMBER_ECHO_HINT} Wait a few seconds after the last button tap and try again.`
            : `No reply text from the server. ${recorded} Some replies, such as the issue list, are sent by the Wacloud rule itself, and a repeat within 10 seconds is ignored.`;
        append(
            { tone: 'customer', text: label || humanize(key) },
            result.reply ? { tone: 'bot', text: result.reply } : { tone: 'secondary', text: silentText }
        );
        loadState(customerId);
    };

    const nuke = async (removeAddresses) => {
        setBusy('nuke');
        const result = await nukeTestCustomer({ userId: customerId, confirmPhone: customer.phone, removeAddresses });
        setBusy(false);
        if (!result) return;
        setShowNuke(false);
        const rows = Object.values(result.deleted).reduce((sum, count) => sum + count, 0);
        append(
            { tone: 'success', text: `Customer reset to new: ${rows} rows removed across ${Object.keys(result.deleted).length} collections.` },
            ...(result.failed.length ? [{ tone: 'danger', text: `These collections could not be cleared: ${result.failed.join(', ')}.` }] : [])
        );
        loadState(customerId);
    };

    const clearCustomer = () => {
        setCustomer(null);
        setTranscript([]);
    };

    const toggleAllow = async (allowed) => {
        setAllowBusy(true);
        const result = await (allowed ? allowWhatsappCustomer(customerId) : removeWhatsappCustomer(customer.phone));
        setAllowBusy(false);
        if (!result) return;
        setCustomer((current) => ({ ...current, allowlisted: allowed }));
        setAllowlistVersion((version) => version + 1);
    };

    const handleAllowlistRemoved = (phone) => {
        setCustomer((current) => (current && current.phone === phone ? { ...current, allowlisted: false } : current));
    };

    const grouped = useMemo(() => {
        if (!flows) return [];
        return JOURNEY_TABS
            .map(({ key, title }) => ({ key, title, flows: flows.filter((flow) => flow.journey === key) }))
            .filter((group) => group.flows.length > 0);
    }, [flows]);

    if (loadFailed) {
        return (
            <Container fluid className="py-4">
                <ErrorBanner message="Failed to load the WhatsApp test flows" onRetry={loadFlows} />
            </Container>
        );
    }
    if (!flows) return <Container fluid className="py-4"><LoadingState /></Container>;

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2 className="mb-1">WhatsApp Test Flows</h2>
                    <p className="text-muted mb-0">
                        Pick any customer, send a flow to their WhatsApp and tap its buttons here. Taps run the real server logic and show the reply below
                        without sending it to the phone. Real taps on the phone work as well; refresh the customer to see them. Stage use only.
                    </p>
                </Col>
            </Row>

            <Card className="shadow-sm mb-4" style={{ position: 'relative', zIndex: 30 }}>
                <Card.Body>
                    <Row className="g-3 align-items-center">
                        <Col xs={12} lg={6}>
                            <CustomerPicker selected={customer} onSelect={setCustomer} onClear={clearCustomer} onNuke={() => setShowNuke(true)} onToggleAllow={toggleAllow} allowBusy={allowBusy} search={searchTestCustomers} />
                        </Col>
                        <Col xs={12} lg="auto">
                            <Form.Check
                                type="switch"
                                id="send-for-real"
                                label="Send to WhatsApp (off records a dry run only)"
                                checked={sendForReal}
                                onChange={(event) => setSendForReal(event.target.checked)}
                            />
                        </Col>
                    </Row>
                    <div className="text-muted small mt-2">
                        Sending bypasses the send window and the minimum gap, and some flows prepare the customer first (reopen their welcome journey, create the
                        offer, give two empty cans for pickups). Customers outside the allowlist are refused by the server.
                    </div>
                </Card.Body>
            </Card>

            <Row className="g-4 mb-4">
                <Col xs={12} lg={6}>
                    <AllowlistPanel refreshKey={allowlistVersion} onRemoved={handleAllowlistRemoved} />
                </Col>
            </Row>

            <Row className="g-4">
                <Col xs={12} xl={7}>
                    {grouped.map((group) => (
                        <Card key={group.key} className="shadow-sm mb-4">
                            <Card.Header className="fw-semibold">{group.title}</Card.Header>
                            <ListGroup variant="flush">
                                {group.flows.map((flow) => (
                                    <FlowRow key={flow.stage} flow={flow} busy={busy} disabled={!customerId} onSend={send} onTap={tap} />
                                ))}
                            </ListGroup>
                        </Card>
                    ))}
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="fw-semibold">Reply with a number</Card.Header>
                        <Card.Body>
                            <div className="text-muted small mb-2">For flows that ask the customer to reply 1, 2, 3 and so on. The number applies to the open conversation shown on the right.</div>
                            <div className="d-flex flex-wrap gap-1">
                                {NUMBER_KEYS.map((key) => (
                                    <Button key={key} size="sm" variant="outline-secondary" disabled={!customerId || Boolean(busy)} onClick={() => tap(key, key.slice(4))}>
                                        {key.slice(4)}
                                    </Button>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} xl={5}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">Conversation</span>
                            <Button size="sm" variant="outline-secondary" disabled={transcript.length === 0} onClick={() => setTranscript([])}>
                                <Trash2 size={14} className="me-1" />Clear
                            </Button>
                        </Card.Header>
                        <Card.Body style={{ minHeight: 160, maxHeight: 420, overflowY: 'auto' }} className="d-flex flex-column">
                            {transcript.length === 0 && <span className="text-muted small">Nothing yet. Send a flow, then tap one of its buttons.</span>}
                            {transcript.map((entry, index) => <TranscriptEntry key={index} entry={entry} />)}
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">Customer state</span>
                            <Button size="sm" variant="outline-secondary" disabled={!customerId} onClick={() => loadState(customerId)}>
                                <RefreshCw size={14} className="me-1" />Refresh
                            </Button>
                        </Card.Header>
                        <Card.Body><CustomerPanel state={state} /></Card.Body>
                    </Card>
                </Col>
            </Row>

            {showNuke && customer && (
                <NukeModal customer={customer} busy={busy === 'nuke'} onCancel={() => setShowNuke(false)} onConfirm={nuke} />
            )}
        </Container>
    );
};

TestFlows.propTypes = {
    getTestFlows: PropTypes.func.isRequired,
    searchTestCustomers: PropTypes.func.isRequired,
    triggerTestFlow: PropTypes.func.isRequired,
    simulateTestTap: PropTypes.func.isRequired,
    nukeTestCustomer: PropTypes.func.isRequired,
    getTestFlowState: PropTypes.func.isRequired,
    allowWhatsappCustomer: PropTypes.func.isRequired,
    removeWhatsappCustomer: PropTypes.func.isRequired
};

export default connect(null, { getTestFlows, searchTestCustomers, triggerTestFlow, simulateTestTap, nukeTestCustomer, getTestFlowState, allowWhatsappCustomer, removeWhatsappCustomer })(TestFlows);
